// Typed fetch client for the serve-manager backend.
//
// - Base URL resolved from env-config.js (window global) with import.meta.env fallback.
// - Operator session bearer token injected on every request.
// - Errors normalized to ApiError (JSON body → {code,message}; non-JSON → http_<status>;
//   thrown fetch → network_error).
// Ported from convenely-admin-app apiFetch, adapted to the path-based Hetzner layout.

import { getApiBase } from './config';
import type { ApiErrorShape } from './types';

export const TOKEN_KEY = 'mgr-app-token';

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/** Normalized error thrown by the client. */
export class ApiError extends Error implements ApiErrorShape {
  readonly status: number;
  readonly code: string;

  constructor(shape: ApiErrorShape) {
    super(shape.message);
    this.name = 'ApiError';
    this.status = shape.status;
    this.code = shape.code;
  }
}

/** Injectable fetch — the real global in prod, a mock in tests. */
export type FetchLike = typeof fetch;

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

export class ApiClient {
  private readonly fetchImpl: FetchLike;

  constructor(fetchImpl?: FetchLike) {
    // Default to the global fetch BOUND to globalThis. A bare `fetch` reference
    // called as `this.fetchImpl(...)` is detached from its Window receiver and
    // throws "Illegal invocation" in the browser (unit tests inject a mock, so
    // they never hit it). Binding is the fix.
    this.fetchImpl = fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  private buildUrl(path: string): string {
    const base = getApiBase().replace(/\/$/, '');
    const suffix = path.startsWith('/') ? path : `/${path}`;
    return `${base}${suffix}`;
  }

  private buildHeaders(hasBody: boolean): Record<string, string> {
    const headers: Record<string, string> = {
      'X-Requested-With': 'XMLHttpRequest',
    };
    if (hasBody) {
      headers['Content-Type'] = 'application/json';
    }
    const token = getStoredToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }

  async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const hasBody = opts.body !== undefined;
    const init: RequestInit = {
      method: opts.method ?? 'GET',
      headers: this.buildHeaders(hasBody),
    };
    if (hasBody) {
      init.body = JSON.stringify(opts.body);
    }
    if (opts.signal) {
      init.signal = opts.signal;
    }

    let response: Response;
    try {
      response = await this.fetchImpl(this.buildUrl(path), init);
    } catch (err) {
      throw new ApiError({
        status: 0,
        code: 'network_error',
        message: err instanceof Error ? err.message : 'Network error',
      });
    }

    if (!response.ok) {
      throw await this.toApiError(response);
    }

    // 204 No Content → no body to parse.
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  private async toApiError(response: Response): Promise<ApiError> {
    try {
      const data = (await response.json()) as Partial<ApiErrorShape>;
      return new ApiError({
        status: response.status,
        code: data.code ?? `http_${response.status}`,
        message: data.message ?? `Request failed (${response.status})`,
      });
    } catch {
      return new ApiError({
        status: response.status,
        code: `http_${response.status}`,
        message: `Request failed (${response.status})`,
      });
    }
  }

  get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'GET', signal });
  }

  post<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'POST', body, signal });
  }

  delete<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'DELETE', signal });
  }
}

/** Shared singleton bound to the real fetch. */
export const api = new ApiClient();
